/**
 * OnboardingModal.js — first-login persona picker (screen `onboarding`).
 *
 * Shown when workspace.use_case is null. Two radio choices: Product (default,
 * existing documentation pipeline) and Design (review-focused flow with Figma
 * import + private hosted review sites + Jira export).
 *
 * Soft gating — the choice sets default UX (sidebar visibility, empty-state
 * copy, new-article landing), but doesn't restrict article types and doesn't
 * touch existing articles or projects (PUT /api/workspace/use-case only stores
 * the persona).
 *
 * The connection list is a read-only preview of the current integration
 * status. Nothing here starts OAuth — connecting happens on /flow, which the
 * setup banner (OnboardingWizard) points to once a persona is stored.
 *
 * "Skip setup", the close button and Escape all dismiss the dialog for this
 * browser session only. The persona stays unset, so the dialog comes back on
 * the next session — the choice is never made on the user's behalf.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import api from '../api';
import { qk } from '../lib/queries';
import { useIntegrations } from '../lib/IntegrationContext';
import Dialog from './Dialog';
import Icon from './Icon';
import LogoIcon from './LogoIcon';
import { Spinner } from './ui';
import '../styles/entry-flow.css';

const SKIP_KEY = 'flow.onboarding.personaSkippedThisSession';

const OPTIONS = [
  {
    id: 'product',
    icon: 'file',
    title: 'Product documentation',
    blurb: 'Help articles, procedures and knowledge bases. Record once, publish to Freshdesk, Confluence or your own hosted docs site.',
  },
  {
    id: 'design',
    icon: 'review',
    title: 'Design reviews',
    blurb: 'Pull frames from Figma, share them for review on a private site, collect comments and approvals, and export approved steps as Jira tasks.',
  },
];

const TOOLS = [
  { id: 'figma',     name: 'Figma',     key: 'figma' },
  { id: 'jira',      name: 'Jira',      key: 'jira' },
  { id: 'freshdesk', name: 'Freshdesk', key: 'freshdesk' },
];

// Focus somewhere meaningful once the dialog is gone. On first load the
// dialog's own trigger is <body>, so restoring to it would leave keyboard
// users nowhere; the page heading is the natural starting point.
function focusPageStart() {
  setTimeout(() => {
    const target = document.querySelector('.main-content h1') || document.querySelector('.main-content');
    if (!target) return;
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: false });
  }, 0);
}

export default function OnboardingModal() {
  const queryClient = useQueryClient();
  const { status: integrations, loading: integrationsLoading } = useIntegrations();
  const [choice, setChoice] = useState('product');
  const firstChoiceRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [skipped, setSkipped] = useState(() => {
    try { return sessionStorage.getItem(SKIP_KEY) === '1'; } catch { return false; }
  });

  useEffect(() => {
    if (skipped) focusPageStart();
  }, [skipped]);

  const pick = async (useCase) => {
    if (submitting) return;
    setError('');
    setSubmitting(true);
    try {
      await api.put('/workspace/use-case', { use_case: useCase });
      // The setup banner reads use_case from the onboarding status, so refresh
      // it too — the banner can then appear as the next step.
      queryClient.invalidateQueries({ queryKey: qk.onboardingStatus() });
      await queryClient.invalidateQueries({ queryKey: qk.workspace() });
      focusPageStart();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not save your choice. Try again.');
      setSubmitting(false);
    }
  };

  const skip = () => {
    if (submitting) return;
    try { sessionStorage.setItem(SKIP_KEY, '1'); } catch { /* still hides for this render */ }
    setSkipped(true);
  };

  if (skipped) return null;

  const onSubmit = (e) => {
    e.preventDefault();
    pick(choice);
  };

  return (
    <Dialog
      open
      onClose={skip}
      closeLabel="Skip setup for now"
      closeOnBackdrop={false}
      maxWidth={800}
      initialFocusRef={firstChoiceRef}
      className="en-onboard"
      title="Make Flow fit your work"
      description="Choose the main way you'll use Flow. You can change this later in Settings."
      footer={(
        <>
          <button type="button" className="btn btn-ghost" onClick={skip} disabled={submitting}>
            Skip setup
          </button>
          <button
            type="submit"
            form="en-onboard-form"
            className="btn btn-primary"
            disabled={submitting}
            aria-busy={submitting || undefined}
          >
            {submitting && <Spinner />}
            {submitting ? 'Saving…' : 'Continue'}
          </button>
        </>
      )}
    >
      <form id="en-onboard-form" onSubmit={onSubmit}>
        <fieldset className="en-persona-set" disabled={submitting}>
          <legend className="visually-hidden">How will you use Flow?</legend>
          <div className="en-persona-grid">
            {OPTIONS.map(opt => (
              <label key={opt.id} className="en-persona" htmlFor={`en-persona-${opt.id}`}>
                <input
                  ref={opt.id === 'product' ? firstChoiceRef : undefined}
                  id={`en-persona-${opt.id}`}
                  type="radio"
                  name="en-persona"
                  value={opt.id}
                  checked={choice === opt.id}
                  onChange={() => { setChoice(opt.id); setError(''); }}
                  className="en-persona-input"
                  aria-describedby={`en-persona-${opt.id}-desc`}
                />
                <span className="en-persona-icon" aria-hidden="true"><Icon name={opt.icon} size={22} /></span>
                <span className="en-persona-title">{opt.title}</span>
                <span className="en-persona-blurb" id={`en-persona-${opt.id}-desc`}>{opt.blurb}</span>
                <span className="en-persona-radio" aria-hidden="true" />
              </label>
            ))}
          </div>
        </fieldset>
        <p className="field-hint">This sets your workspace's default views. Existing articles and projects stay as they are.</p>

        {error && (
          <div className="inline-msg is-error en-onboard-error" role="alert">
            <Icon name="alert" /> <span>{error}</span>
          </div>
        )}
      </form>

      <section className="en-tools" aria-labelledby="en-tools-title">
        <h3 id="en-tools-title" className="en-tools-title">Connect your tools when you are ready</h3>
        <p className="en-tools-sub">
          Nothing connects from here. After you continue, the setup banner takes you to the Flow page, where each tool has its own Connect step.
        </p>
        <ul className="en-tools-list">
          {TOOLS.map(t => {
            const connected = !!integrations?.[t.key];
            return (
              <li key={t.id} className="en-tool">
                <LogoIcon id={t.id} size={24} decorative />
                <span className="en-tool-name">{t.name}</span>
                <span className={`en-tool-state ${connected ? 'is-on' : ''}`}>
                  {integrationsLoading
                    ? 'Checking…'
                    : connected
                      ? <><Icon name="checkCircle" size={14} /> Connected</>
                      : 'Not connected'}
                </span>
              </li>
            );
          })}
        </ul>
      </section>
    </Dialog>
  );
}
